import React, { useState, useEffect } from "react";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import EmptyState from "../../components/common/EmptyState";
import PageHeader from "../../components/common/PageHeader";
import { managementFeesApi, buildingsApi } from "../../services/api";
import { formatCurrency } from "../../utils/formatters";

interface ManagementFeeAllocation {
  id: string;
  ownerId: string;
  buildingId: string;
  name: string;
  percentage?: number;
  fixedAmount?: number;
  frequency: string;
  status: string;
  effectiveFrom: string;
  effectiveTo?: string;
  createdAt: string;
}

interface Building {
  id: string;
  name: string;
}

const ManagementFeesList: React.FC = () => {
  const [allocations, setAllocations] = useState<ManagementFeeAllocation[]>([]);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateResult, setGenerateResult] = useState<any>(null);
  const [form, setForm] = useState({
    buildingId: "",
    name: "",
    percentage: "",
    fixedAmount: "",
    frequency: "monthly",
    effectiveFrom: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [allocs, bldgs] = await Promise.all([
          managementFeesApi.getAll(),
          buildingsApi.getAll(),
        ]);
        setAllocations(Array.isArray(allocs) ? allocs : []);
        setBuildings(Array.isArray(bldgs) ? bldgs : []);
      } catch (err) {
        console.error("Error fetching management fees:", err);
        setError("Failed to load management fee allocations.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data: any = {
        buildingId: form.buildingId,
        name: form.name,
        frequency: form.frequency,
        effectiveFrom: form.effectiveFrom,
      };
      if (form.percentage) data.percentage = parseFloat(form.percentage);
      if (form.fixedAmount) data.fixedAmount = parseInt(form.fixedAmount, 10);

      await managementFeesApi.create(data);
      setShowForm(false);
      setForm({
        buildingId: "",
        name: "",
        percentage: "",
        fixedAmount: "",
        frequency: "monthly",
        effectiveFrom: new Date().toISOString().split("T")[0],
      });
      // Refresh
      const allocs = await managementFeesApi.getAll();
      setAllocations(Array.isArray(allocs) ? allocs : []);
    } catch (err) {
      console.error("Error creating allocation:", err);
      setError("Failed to create allocation.");
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await managementFeesApi.delete(id);
      setAllocations((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      console.error("Error deleting allocation:", err);
      setError("Failed to delete allocation.");
    }
  };

  const handleGenerate = async (dryRun: boolean) => {
    try {
      setGenerating(true);
      const result = await managementFeesApi.generate({ dryRun });
      setGenerateResult(result);
      if (!dryRun) {
        // Refresh allocations after generation
        const allocs = await managementFeesApi.getAll();
        setAllocations(Array.isArray(allocs) ? allocs : []);
      }
    } catch (err) {
      console.error("Error generating fees:", err);
      setError("Failed to generate management fees.");
    } finally {
      setGenerating(false);
    }
  };

  const getBuildingName = (buildingId: string) => {
    const building = buildings.find((b) => b.id === buildingId);
    return building?.name || buildingId;
  };

  if (loading) return <Spinner />;

  return (
    <div>
      <PageHeader
        title="Management Fee Allocations"
        description="Configure how company expenses are allocated across buildings"
        actions={
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
          >
            New Allocation
          </button>
        }
      />

      {error && <ErrorAlert message={error} />}

      {/* Generate Fees Section */}
      <div className="mb-6 bg-white shadow rounded-lg p-4">
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Generate Management Fee Bills
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Generate bills for each building based on active allocation rules and
          current company expenses.
        </p>
        <div className="flex space-x-2">
          <button
            onClick={() => handleGenerate(true)}
            disabled={generating}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            {generating ? "Processing..." : "Preview"}
          </button>
          <button
            onClick={() => handleGenerate(false)}
            disabled={generating}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {generating ? "Processing..." : "Generate Bills"}
          </button>
        </div>

        {generateResult && (
          <div className="mt-4 p-3 bg-gray-50 rounded-md">
            <p className="text-sm font-medium text-gray-700 mb-2">
              {generateResult.dryRun ? "Preview:" : "Generated:"}{" "}
              {generateResult.message}
            </p>
            {generateResult.bills?.length > 0 && (
              <ul className="text-sm text-gray-600 space-y-1">
                {generateResult.bills.map((bill: any) => (
                  <li key={bill.id}>
                    {getBuildingName(bill.buildingId)} —{" "}
                    {formatCurrency((bill.amount ?? 0) / 100)} — {bill.title}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Create Form */}
      {showForm && (
        <div className="mb-6 bg-white shadow rounded-lg p-4">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            New Allocation
          </h3>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Building
                </label>
                <select
                  value={form.buildingId}
                  onChange={(e) =>
                    setForm({ ...form, buildingId: e.target.value })
                  }
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                >
                  <option value="">Select building...</option>
                  {buildings.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Name
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  placeholder="e.g. General Management"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Percentage (%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={form.percentage}
                  onChange={(e) =>
                    setForm({ ...form, percentage: e.target.value })
                  }
                  placeholder="e.g. 40"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Fixed Amount (pence)
                </label>
                <input
                  type="number"
                  min="0"
                  value={form.fixedAmount}
                  onChange={(e) =>
                    setForm({ ...form, fixedAmount: e.target.value })
                  }
                  placeholder="e.g. 50000 (£500)"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Frequency
                </label>
                <select
                  value={form.frequency}
                  onChange={(e) =>
                    setForm({ ...form, frequency: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annually">Annually</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Effective From
                </label>
                <input
                  type="date"
                  value={form.effectiveFrom}
                  onChange={(e) =>
                    setForm({ ...form, effectiveFrom: e.target.value })
                  }
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
              >
                Create
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Allocations List */}
      {allocations.length === 0 ? (
        <EmptyState
          title="No allocations"
          description="Create allocation rules to distribute company expenses across buildings."
        />
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Building
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Allocation
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Frequency
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Effective From
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {allocations.map((alloc) => (
                <tr key={alloc.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {getBuildingName(alloc.buildingId)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {alloc.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {alloc.percentage !== undefined
                      ? `${alloc.percentage}%`
                      : alloc.fixedAmount !== undefined
                      ? formatCurrency(alloc.fixedAmount / 100)
                      : "—"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                    {alloc.frequency}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        alloc.status === "active"
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {alloc.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {alloc.effectiveFrom}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                    <button
                      onClick={() => handleDelete(alloc.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ManagementFeesList;
